import { Fragment, memo, useState, useEffect, useCallback, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import Webcam from "react-webcam";
import { isMobile } from 'react-device-detect';

import Icon from 'app/components/Icon';
import Modal from 'app/components/Modal';
import Alert from 'app/components/Alert';
import { useWindowSize } from "app/hooks/useWindowSize";
import Api from 'app/utils/Api';

import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const PhotoUploader = ({ title="Photo", icon="photo", heading = null, url=null, button, width=800, height=800, onSuccess, onError, camera = false, crop = false, imgSize = 8, minWidth = 300, btnText = null }) => {  

    const dispatch = useDispatch();
    const windowSize = useWindowSize();
  
    width = parseInt(width);
    height = parseInt(height || width);

    const cropperRef = useRef(null);
    const refContainer = useRef(null);
    const refBox = useRef(null);
    const webcamRef = useRef(null);

    const [open,setOpen] = useState(false);
    const [saving,setSaving] = useState(false);
    const [uploaded,setUploaded] = useState(false);
    const [progress, setProgress]= useState(0);
    const [error,setError] = useState(false);
    const [img,setImg] = useState(null);
    //const [cropData,setCrop] = useState(null);
    const [cropperHeight, setCropperHeight]= useState(0);
    const [containerWidth,setContWidth] = useState(0);
    const [dimensions,setDimensions] = useState({loaded: false, width:0, height:0});
    const [camDimensions,setCamDimensions] = useState({width:0, height:0});
    const [allowCamera,setAllowCamera] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [mirrored, setMirrored] = useState(false);
    const [switchCamera, setSwitchCamera] = useState(false);

    const setRunning = (state) => dispatch({ type: 'RUNNING', payload: state });

    useEffect(()=>{
        setImg(null);
        setError(false);
        return ()=> hideModal();
    },[]);

    useEffect(() => {
        if( img ){
            getImageSize(img).then(_img=>{
                setDimensions(_img);
                if( (_img.width || 0) < minWidth ){
                    setError(`Image is small, image size must be more then ${minWidth}px`);
                    setImg(null);
                } else {
                    getContainerWidth();
                    if( !_img.loaded ){ setError(_img.message); setImg(null); }
                }
            });
        }
    }, [img]);

    useEffect(()=>{
        let ch = 0;
        if( dimensions.width && dimensions.height && containerWidth )
            ch = Math.floor(containerWidth / dimensions.width * dimensions.height);
        if( ch === 0) ch = 350;
        if( ch < 120 ) ch = 120;
        if( ch > 400 ) ch = 400;

        setCropperHeight(ch);
    },[dimensions,containerWidth]);

    const getContainerWidth = () => {
        if( refContainer?.current )
            setContWidth(refContainer.current.offsetWidth);
    }

    const openCamera = () =>{
        setError(false);
        setImg(null);
        
        let sratio = windowSize.ratio; //isMobile ? 2/3 : 3/2;
        let width = refBox?.current?.offsetWidth || 0;
        height = Math.floor((width+32) / sratio);
        if( height < 200) height = 200;
        setCamDimensions({ ratio: 1/sratio, height });
        setShowCamera(true);
    }
    const onBtnClick = () =>{
        setRunning(false);    
        setOpen(true); 
        try{
            if( camera )
               navigator.mediaDevices.enumerateDevices().then((mdvc)=>setAllowCamera(mdvc.filter(({kind})=>kind==="videoinput").length > 0));
        } catch (e){ }
    }

    const hideModal = () =>{
        setOpen(false);
        setSaving(false);
        setUploaded(false);
        setProgress(0);
        setError(false);
        setImg(null);
       // setCrop(null);
        setDimensions({loaded: false, width:0, height:0});
        setRunning(true);
        setAllowCamera(false);
        setShowCamera(false);
        setMirrored(false);
        setShowCamera(false);
        setSwitchCamera(false);
    }

    const captureImage = useCallback(async() => {
        const imageSrc = webcamRef.current.getScreenshot();
        if( imageSrc ){
            var fresp = await fetch(imageSrc);
            var fblob = await fresp.blob();
            if( fblob ){
                let file  = new File([fblob], `cam_photo_${new Date().getTime()}.jpg`,{type:fblob.type, lastModified: new Date().getTime()});
                setImg(file);
                setShowCamera(false);
            } else
                setError("Unable to load photo, try again");
        } else
            setError("Unable to capture photo, try again");
    },[webcamRef]);

    const getImageSize = (url) => {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined') 
                resolve({ loaded:false, message: "Unable to load image!"});

            let timer = null;

            const _img = new Image();
            _img.addEventListener('load', () => {
              if (timer)
                clearTimeout(timer);
              resolve({ loaded:true, width: _img?.naturalWidth || 0, height: _img?.naturalHeight || 0 });
            });

            _img.addEventListener('error', (event) => {
              if (timer) 
                clearTimeout(timer);
              resolve({ loaded:false, message: event.message });
            });

            _img.src = URL.createObjectURL(url);

            setTimeout(() =>resolve({ loaded:false, message: "Unable to load image"}), 10000);
        });
    }

    const renderButton = () =>{
        return button({ onClick:()=>onBtnClick() });
    };

    const onSelectFiles = (e) => {

        setError(false);
        setImg(null);

        var _error = false, 
            file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        if( !file ) return null;

        var _ext = /[^.]+$/.exec((file?.name || '').toString().trim()).toString().toLowerCase(), 
            _size = parseInt(file?.size || 0) / (1024*1024); 

        if( _ext === '' || _size === 0)
            _error = "Image is not valid";
        else if ( !['jpeg','jpg','png'].includes(_ext) )
            _error = "Image type is not valid, only allowed jpg or png";
        else if ( _size > imgSize )
            _error = `Image size must be less or equal to ${imgSize}Mb`;
        
        if( _error )
            setError(_error);
        else {
            setImg(file);
        }
    }

    const onSave = async() =>{
    
        let cropper = null, idata = {}, cdata = {}, _data = {};
        var file;

        if( crop ){
            cropper = cropperRef.current?.cropper;
            cdata = cropper.getData(); 
            idata = cropper.getImageData();
        }

        try{
                
            if( crop ){
                var fresp = await fetch(cropper.getCroppedCanvas().toDataURL());
                var fblob = await fresp.blob();
                var fname= (img?.name || "photo").replace('.jpeg','').replace('.jpg','').replace('.png','');
                file = new File([fblob], `${fname}.png`, { type: 'image/png' });
            
                _data = {
                    x: Math.floor(cdata?.x || 0),
                    y: Math.floor(cdata?.y || 0),
                    cwidth: Math.floor(cdata?.width || 0),
                    cheight: Math.floor(cdata?.height || 0),
                    iwidth: Math.floor(idata?.width || 0),
                    iheight: Math.floor(idata?.height || 0),
                    owidth: Math.floor(idata?.naturalWidth || 0),
                    oheight: Math.floor(idata?.naturalHeight || 0),
                    ratio: cdata.aspectRatio || 1,
                };
            } else {
                const blob = new Blob([img],{type: img?.type});
                file = new File([blob], img?.name, { type:img?.type });
            }

            if( !url ){
                hideModal();
                return onSuccess({file, data:_data});
            }

            setError(false);
            setSaving(true);
            setUploaded(false);
            setProgress(0);

            var config = {
                onUploadProgress: (e)=>{
                    var per = Math.floor( (e.loaded*100) / e.total);
                    if( per === 100 ) per= 99;
                        setProgress(per);
                },
            };

            var res = await Api.upload(url, 'image', [file], {cropData:_data}, config);
            setSaving(false);
            
            if( res.success ){
                setUploaded(true);
                if( onSuccess ) onSuccess(res.data);
                setTimeout(()=>hideModal(),5000);

            } else {
                if( onError ) onError(res.message || `Unable to save ${title.toLowerCase()}`); 
                setError(res.message || `Unable to save ${title.toLowerCase()}`);
            }
    
        } catch {
            setError(`Unable to save ${title.toLowerCase()}`);
            setSaving(false);
        }
    }

    const renderFooter = () =>{
        return <div>
            {allowCamera && <LoadingButton variant="outlined" style={{ position:'absolute', left:"16px" }} onClick={()=>openCamera()} size="small"><span>Cancel</span></LoadingButton>}
            <LoadingButton 
                loading={saving} 
                variant="contained" 
                onClick={()=>onSave()} 
                size="small"
            >
                <span>{btnText || 'Save'}</span>
            </LoadingButton>
        </div>;
    }

    if( !button )
        return null;

    return <Fragment>
        {renderButton()}
        <Modal 
            open={open} 
            onClose={()=>hideModal()}
            titleIcon={icon}
            title={heading || `Change ${title}`} 
            fullWidth={false}
            padding={showCamera ? 0 : 16}
            headerSize={15}
            disableEscapeKey={false}
            stopTransition={true}
            disableBackdrop={true}
            footer={img && !uploaded ? renderFooter() : null}
        >
            <div className={`box_photouploader${showCamera ? ' cam' : ''}`} ref={refBox}> 
                {error && <Alert severity="error">{error}</Alert>}
                {!error && uploaded && <Alert severity="success">{title} changed successfully !</Alert>}

                {!uploaded && <Fragment>

                    {img && dimensions.loaded ? <div className="container" ref={refContainer}>

                       {saving && <div className="loader">
                            <div className="crl"></div>
                            <CircularProgress variant="determinate" size={140} value={progress} />
                            <div className="per">{progress}%</div>
                        </div>}

                        {crop ? <Cropper
                          src={URL.createObjectURL(img)}
                          style={{ height: cropperHeight, width: "100%" }}
                          aspectRatio={width / height}
                          guides={false}
                          scaleX={1}
                          scaleY={1}
                          viewMode={1}
                          ref={cropperRef}
                          minCropBoxWidth={50}
                          minCropBoxHeight={50}
                        /> : <div style={{display:'flex',justifyContent:'center'}}>
                            <img alt='Cropper' src={URL.createObjectURL(img)} style={{height: cropperHeight, maxWidth: "100%" }}/>
                        </div>}
                        
                    </div>
                    :
                    <Fragment>  
                        {showCamera ?
                            <div className="camera-box">
                                <Webcam
                                    audio={false}
                                    screenshotFormat="image/jpeg"
                                    ref={webcamRef}
                                    screenshotQuality={1}
                                    width="100%"
                                    height={camDimensions?.height || "100%"}
                                    style={{display:"flex"}}
                                    mirrored={mirrored}
                                    forceScreenshotSourceSize={true}
                                    onUserMediaError={(e)=>setError("Unable to open your Camera")}
                                    videoConstraints={{ 
                                        aspectRatio: camDimensions?.ratio || 1,
                                        facingMode: switchCamera ? {exact: "environment"} : "user",
                                    }}
                                />
                                <div className="cam-cntr">
                                  <div>
                                    {isMobile && <Icon className="icn" type="r" onClick={()=>setSwitchCamera(!switchCamera)}>sync</Icon>}
                                  </div>
                                  <button className="capture" onClick={captureImage}><span></span></button>
                                  <div>
                                    <Icon className={`icn fa-rotate-${mirrored ? 270 :90}`} type="r" onClick={()=>setMirrored(!mirrored)}>hourglass-start</Icon>
                                  </div>
                                </div>
                            </div>
                        : <div className="btns">
                            {allowCamera && <div className="browse_btn" onClick={()=>openCamera()}>
                                <Icon size={22}>{icon}</Icon>
                                <span>Open Camera</span>
                            </div>}
                            <div className="browse_btn">
                                <input type="file" accept="image/*" onChange={(e)=>onSelectFiles(e)} />
                                <Icon size={22}>{allowCamera ? 'image' : icon}</Icon>
                                <span>{isMobile ? 'Open Gallery' : 'Click or Drag here'}</span>
                            </div>
                        </div>}
                    </Fragment>
                    }
                </Fragment>}
            </div>

        </Modal>
    </Fragment>;
}

export default memo(PhotoUploader);